import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { applyReservationDuration, findReservationType, legacyReservationType } from '../utils/reservationTypes';
import { RESERVATION_CHANGE_REASONS, requiresReservationReason } from '../utils/reservationChangePolicy';
import { schoolWallClockToIso } from '../utils/locationSchedule';
import { getAircraftLocationAvailabilityError } from '../utils/locationScheduleReconciliation';

const personName = person => `${person?.firstName || person?.first_name || ''} ${person?.lastName || person?.last_name || ''}`.trim();
const clockTime = value => value == null ? '' : String(value).includes(':') ? String(value).slice(0, 5) : `${String(Math.floor(Number(value) / 100)).padStart(2, '0')}:${String(Number(value) % 100).padStart(2, '0')}`;

const MobileReservationForm = ({ selectedDate, reservation = null, onClose, onSuccess }) => {
  const { currentUser, aircraft, people, roles, reservationTypes = [], fetchReservationTypes, reservationTypesError, loadingReservationTypes, selectedLocationId, locationResources, locations = [],
    locationAircraftSchedules, locationAircraftSchedulesError, loadingLocationAircraftSchedules, fetchLocationAircraftSchedules } = useApp();
  const dateValue = selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : '';
  const locationId = reservation?.locationId || selectedLocationId;
  const location = locations.find(item => String(item.id) === String(locationId));
  const scoped = String(locationResources?.locationId) === String(locationId);
  const availableAircraft = locationId && !location?.isDefault ? (scoped ? locationResources.aircraft : []) : aircraft;
  const locationInstructors = locationId && !location?.isDefault ? (scoped ? locationResources.instructors : []) : people;
  const originalInstructor = (Array.isArray(people) ? people : []).find(person => String(person.id) === String(reservation?.userIdInstructor)) || reservation?.instructor;
  const originalClient = (Array.isArray(people) ? people : []).find(person => String(person.id) === String(reservation?.userIdClient)) || reservation?.client;
  const [formData, setFormData] = useState({
    date: reservation?.startDate ? String(reservation.startDate).slice(0, 10) : dateValue,
    time: clockTime(reservation?.startTime),
    endDate: reservation?.endDate ? String(reservation.endDate).slice(0, 10) : reservation?.startDate ? String(reservation.startDate).slice(0, 10) : dateValue,
    endTime: clockTime(reservation?.endTime),
    instructor: personName(originalInstructor) || (reservation?.userIdInstructor ? reservation.instructorName || `Instructor ${reservation.userIdInstructor}` : ''),
    aircraft: reservation?.aircraftId ? String(reservation.aircraftId) : '',
    student: personName(originalClient) || reservation?.clientName || (reservation?.userIdClient ? `Client ${reservation.userIdClient}` : ''),
    type: legacyReservationType(reservation?.type),
    reservationTypeId: reservation?.reservationTypeId ?? null,
    comments: reservation?.comment || ''
  });
  const originalForm = React.useRef(formData);
  const aircraftLocationError = aircraftId => getAircraftLocationAvailabilityError({
    aircraftId, locationId, locations, schedules: locationAircraftSchedules,
    error: locationAircraftSchedulesError, loading: loadingLocationAircraftSchedules,
    start: new Date(schoolWallClockToIso(formData.date, formData.time)),
    end: new Date(schoolWallClockToIso(formData.endDate, formData.endTime))
  });
  const completionTimer = React.useRef(null);
  useEffect(() => () => clearTimeout(completionTimer.current), []);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [saved, setSaved] = useState(false);
  const canEdit = !reservation || (Number(reservation.safetyPilotId) !== Number(currentUser?.user?.id)
    && (currentUser?.user?.orgRole !== 'client' || String(reservation.userIdClient) === String(currentUser?.user?.id)));
  const hasChanges = Object.keys(formData).some(key => String(formData[key] ?? '') !== String(originalForm.current[key] ?? ''));
  const needsReason = !!reservation && hasChanges && requiresReservationReason(reservation);
  const selectedReservationType = findReservationType(reservationTypes, formData);
  useEffect(() => { fetchReservationTypes?.(); }, [fetchReservationTypes]);

  const activeInstructors = (Array.isArray(locationInstructors) ? locationInstructors : []).filter(person => {
    if (locationId && !location?.isDefault) return String(person.status).toLowerCase() === 'active';
    if (roles && roles.error) {
      return person.roleId === 3 && person.status === 'active';
    }
    let rolesList = [];
    if (Array.isArray(roles)) {
      rolesList = roles;
    } else if (roles && typeof roles === 'object') {
      rolesList = Object.values(roles);
    }
    return rolesList.find(r => r.id === person.roleId)?.name === 'instructor' && person.status === 'active';
  });

  useEffect(() => {
    if (!reservation && currentUser?.user?.orgRole === 'client') {
      setFormData(prev => ({
        ...prev,
        student: `${currentUser?.user?.firstName || currentUser?.firstName || ''} ${currentUser?.user?.lastName || currentUser?.lastName || ''}`.trim()
      }));
    }
  }, [currentUser, reservation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || saved) return;
    if (!canEdit) { setMessage('You cannot edit this reservation.'); setIsError(true); return; }
    if (!reservation && (loadingReservationTypes || reservationTypesError || !selectedReservationType)) {
      setMessage(reservationTypesError || 'Select an organization reservation type before saving.');
      setIsError(true);
      return;
    }
    
    if (!formData.instructor && !formData.aircraft) {
      setMessage('Error: Please select at least an instructor or an aircraft');
      setIsError(true);
      return;
    }
    if (!formData.date || !formData.time || !formData.endDate || !formData.endTime || new Date(`${formData.endDate}T${formData.endTime}`) <= new Date(`${formData.date}T${formData.time}`)) {
      setMessage('Select a valid start and end time, with the end after the start.'); setIsError(true); return;
    }
    if (['Flight Training', 'Discovery', 'Ground lesson', 'Checkride'].includes(formData.type) && !formData.instructor) {
      setMessage('An instructor is required for this reservation type.'); setIsError(true); return;
    }
    const aircraftError = aircraftLocationError(formData.aircraft);
    if (aircraftError) { setMessage(aircraftError); setIsError(true); return; }
    const finalReason = (reason === 'other' ? customReason : reason).trim();
    const needsReasonAtSubmit = !!reservation && hasChanges && requiresReservationReason(reservation);
    if (needsReasonAtSubmit && !finalReason) { setMessage('A reason is required for changes within 28 hours.'); setIsError(true); return; }
    
    setIsSubmitting(true);
    setMessage('');
    setIsError(false);
    
    const convertTimeToMilitary = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 100 + minutes;
    };
    
    const instructorId = activeInstructors.find(person => personName(person) === formData.instructor)?.id
      || (reservation && formData.instructor === originalForm.current.instructor ? reservation.userIdInstructor : null) || null;
    const clientId = (Array.isArray(people) ? people : []).find(person => personName(person) === formData.student)?.id
      || (reservation && formData.student === originalForm.current.student ? reservation.userIdClient : null)
      || (!reservation && currentUser?.user?.orgRole === 'client' ? currentUser.user.id : null) || null;
    
    const payload = {
      ...(locationId ? { locationId: Number(locationId) } : {}),
      ...(reservation ? { id: reservation.id, safetyPilotId: formData.type === 'Rental' ? reservation.safetyPilotId ?? null : null, renterChargePercentage: formData.type === 'Rental' ? reservation.renterChargePercentage ?? null : null, ...(Array.isArray(reservation.maneuvers) ? { maneuvers: reservation.maneuvers.map(item => ({ id: item.id })) } : {}) } : {}),
      startDate: formData.date,
      startTime: convertTimeToMilitary(formData.time),
      endDate: formData.endDate,
      endTime: convertTimeToMilitary(formData.endTime),
      userIdInstructor: instructorId,
      aircraftId: formData.aircraft || null,
      userIdClient: clientId,
      comment: formData.comments,
      type: formData.type,
      reservationTypeId: selectedReservationType?.id ?? reservation?.reservationTypeId ?? null,
      status: reservation?.status || 'active',
      ...(needsReasonAtSubmit ? { rescheduleReason: finalReason } : {})
    };
    
    try {
      const response = await fetch(`${API_BASE_URL}/reservation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setMessage(reservation ? 'Reservation Updated Successfully' : 'Reservation Created Successfully');
        setSaved(true);
        setIsError(false);
        onSuccess();
        completionTimer.current = setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorText = await response.text();
        let errorMessage = reservation ? 'Failed to update reservation' : 'Failed to create reservation';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        setMessage(`Error: ${errorMessage}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage(reservation ? 'Error: Error updating reservation' : 'Error: Error creating reservation');
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ height: '100dvh', backgroundColor: 'white', display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div style={{ padding: '15px', backgroundColor: '#007bff', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>{reservation ? 'Update Reservation' : 'New Reservation'}</h3>
        <button type="button" aria-label="Close reservation" title="Close" disabled={isSubmitting} onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>×</button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '15px' }}>
        <form id="mobile-reservation-form" onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Start Date:</label>
            <input 
              type="date" 
              value={formData.date} 
              onChange={(e) => {
                const newStartDate = e.target.value;
                setFormData({...formData, date: newStartDate, endDate: newStartDate});
              }} 
              required 
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Start Time:</label>
            <select 
              value={formData.time} 
              onChange={(e) => {
                const newTime = e.target.value;
                const updatedFormData = applyReservationDuration({ ...formData, time: newTime }, selectedReservationType);
                setFormData(updatedFormData);
              }} 
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">Select Time</option>
              {[...new Set([...Array.from({length: 24}, (_, h) => [
                `${h.toString().padStart(2, '0')}:00`,
                `${h.toString().padStart(2, '0')}:30`
              ]).flat(), ...(formData.time ? [formData.time] : [])])].sort().map(time => {
                const [hour, minute] = time.split(':');
                const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const ampm = hour < 12 ? 'AM' : 'PM';
                const display = `${h12}:${minute} ${ampm}`;
                return <option key={time} value={time}>{display}</option>;
              })}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>End Time:</label>
            <select 
              value={formData.endTime} 
              onChange={(e) => setFormData({...formData, endTime: e.target.value})} 
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">Select Time</option>
              {[...new Set([...Array.from({length: 24}, (_, h) => [
                `${h.toString().padStart(2, '0')}:00`,
                `${h.toString().padStart(2, '0')}:30`
              ]).flat(), ...(formData.endTime ? [formData.endTime] : [])])].sort().map(time => {
                const [hour, minute] = time.split(':');
                const h12 = hour == 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const ampm = hour < 12 ? 'AM' : 'PM';
                const display = `${h12}:${minute} ${ampm}`;
                return <option key={time} value={time}>{display}</option>;
              })}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>End Date:</label>
            <input aria-label="End date" type="date" value={formData.endDate} onChange={event => setFormData({ ...formData, endDate: event.target.value })} required style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Instructor:</label>
            <select 
              value={formData.instructor} 
              onChange={(e) => setFormData({...formData, instructor: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">Select Instructor</option>
              {formData.instructor === originalForm.current.instructor && formData.instructor && !activeInstructors.some(person => personName(person) === formData.instructor) && <option value={formData.instructor}>{formData.instructor}</option>}
              {activeInstructors.map(instructor => {
                const displayName = `${instructor.firstName} ${instructor.lastName}`;
                return <option key={instructor.id} value={displayName}>{displayName}</option>;
              })}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Aircraft:</label>
            <select 
              value={formData.aircraft} 
              onChange={(e) => setFormData({...formData, aircraft: e.target.value})}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">Select Aircraft</option>
              {reservation?.aircraftId && !(Array.isArray(availableAircraft) ? availableAircraft : []).some(plane => String(plane.id) === String(reservation.aircraftId)) && <option value={String(reservation.aircraftId)}>{reservation.aircraft?.model || 'Aircraft'} - {reservation.aircraft?.tailNumber || reservation.aircraftId}</option>}
              {(Array.isArray(availableAircraft) ? availableAircraft : []).filter(plane => {
                if (reservation?.aircraftId != null && String(plane.id) === String(reservation.aircraftId)) return true;
                if (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') {
                  return true;
                }
                return plane.status !== 'Unavailable';
              }).map(plane => (
                <option key={plane.id} value={plane.id}
                  disabled={!!(formData.time && formData.endTime && aircraftLocationError(plane.id))}
                  title={formData.time && formData.endTime ? aircraftLocationError(plane.id) || undefined : undefined}>{plane.model} - {plane.tailNumber || plane.registration}</option>
              ))}
            </select>
            {locations.length > 1 && loadingLocationAircraftSchedules && <p role="status">Loading aircraft location schedules...</p>}
            {locations.length > 1 && locationAircraftSchedulesError && <p role="alert">{locationAircraftSchedulesError} <button type="button" onClick={fetchLocationAircraftSchedules}>Reload aircraft schedules</button></p>}
            {formData.aircraft && formData.time && formData.endTime && !loadingLocationAircraftSchedules && !locationAircraftSchedulesError && aircraftLocationError(formData.aircraft) && <p role="status">{aircraftLocationError(formData.aircraft)}</p>}
          </div>

          {currentUser?.user?.orgRole !== 'client' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Client:</label>
              <select 
                value={formData.student} 
                onChange={(e) => setFormData({...formData, student: e.target.value})}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="">Select Client</option>
                {reservation && formData.student === originalForm.current.student && formData.student && !(Array.isArray(people) ? people : []).some(person => personName(person) === formData.student) && <option value={formData.student}>{formData.student}</option>}
                {(Array.isArray(people) ? people : []).map(person => {
                  const displayName = `${person.firstName || person.first_name || ''} ${person.lastName || person.last_name || ''}`.trim();
                  return <option key={person.id} value={displayName}>{displayName}</option>;
                })}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Type:</label>
            <select aria-label="Reservation type"
              value={selectedReservationType ? String(selectedReservationType.id) : reservation ? 'historical' : ''}
              disabled={loadingReservationTypes}
              onChange={(e) => {
                const selectedType = reservationTypes.find(type => String(type.id) === e.target.value);
                const updatedFormData = applyReservationDuration({ ...formData, type: legacyReservationType(selectedType?.name), reservationTypeId: selectedType?.id ?? null }, selectedType);
                setFormData(updatedFormData);
              }} 
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="">Select Type</option>
              {reservation && !selectedReservationType && <option value="historical">{formData.type || 'Existing reservation type'}</option>}
              {reservationTypes.map(type => <option key={type.id} value={String(type.id)}>{type.name}</option>)}
            </select>
            {loadingReservationTypes && <p role="status">Loading reservation types...</p>}
            {reservationTypesError && <p role="alert">{reservationTypesError} <button type="button" onClick={fetchReservationTypes}>Retry</button></p>}
            {!loadingReservationTypes && !reservationTypesError && !reservationTypes.length && <p>No reservation types configured.</p>}
            {selectedReservationType?.description && <p>{selectedReservationType.description}</p>}
            {selectedReservationType?.cost != null && selectedReservationType.cost !== '' && <p>Cost: ${selectedReservationType.cost}</p>}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Comments:</label>
            <textarea 
              value={formData.comments} 
              onChange={(e) => setFormData({...formData, comments: e.target.value})} 
              rows="3" 
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }} 
            />
          </div>

          {needsReason && <fieldset style={{ margin: '0 0 1rem', padding: '0.75rem', border: '1px solid #ccc', borderRadius: '4px' }}>
            <legend>Reservation change reason</legend>
            <select aria-label="Reservation change reason" value={reason} onChange={event => setReason(event.target.value)} required style={{ width: '100%', padding: '8px' }}>
              <option value="">Select a reason</option>
              {RESERVATION_CHANGE_REASONS.map(item => <option key={item} value={item}>{item === 'other' ? 'Other' : item}</option>)}
            </select>
            {reason === 'other' && <input aria-label="Other change reason" value={customReason} onChange={event => setCustomReason(event.target.value)} required placeholder="Please specify..." style={{ width: '100%', boxSizing: 'border-box', padding: '8px', marginTop: '0.5rem' }} />}
          </fieldset>}
          {message && (
            <div style={{ 
              padding: '10px', 
              marginBottom: '15px', 
              borderRadius: '4px', 
              backgroundColor: isError ? '#f8d7da' : '#d4edda', 
              color: isError ? '#721c24' : '#155724', 
              border: `1px solid ${isError ? '#f5c6cb' : '#c3e6cb'}` 
            }}>
              {message}
            </div>
          )}

        </form>
      </div>
      <div className="mobile-reservation-footer" style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid #d6dce1', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
        <button type="button" onClick={onClose} disabled={isSubmitting} style={{ flex: 1, padding: '0.75rem 1rem', backgroundColor: 'white', color: '#495057', border: '1px solid #adb5bd', borderRadius: '6px', fontSize: '16px', fontWeight: '600' }}>{reservation ? 'Close' : 'Cancel'}</button>
        <button type="submit" form="mobile-reservation-form" disabled={isSubmitting || saved || !canEdit} style={{ flex: 1, padding: '0.75rem 1rem', backgroundColor: isSubmitting ? '#6c757d' : '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>{isSubmitting ? 'Saving...' : reservation ? 'Update' : 'Save Reservation'}</button>
      </div>
    </div>
  );
};

export default MobileReservationForm;